/**
 * Pieces of a thread both layouts draw the same way: the day separator, a
 * rejection's system entry and the jump-to-message flash.
 */
import { X } from '../../../icons';
import { actorLabel, formatDayLabel, formatTime, isMine, OWN_SENDER_LABEL, type ChatThreadEntry } from './chatEntries';

/** The message a reply preview, marker or "View comment" jumped to; the nonce restarts a repeat flash. */
export interface FlashTarget {
  id: string;
  nonce: number;
}

const FLASH_ANIMATION_STYLE = { animation: 'chat-jump-flash 1.6s ease-out forwards' };

/**
 * A soft tinted overlay fading to nothing over the message jumped to — not a
 * border/ring, which reads as a validation state. Keyed by the nonce so a
 * repeat jump remounts it and restarts its animation (see `jumpToMessage`).
 */
export function JumpFlash({ target, messageId }: { target: FlashTarget | null; messageId: string }) {
  if (target?.id !== messageId) return null;
  return (
    <div
      key={target.nonce}
      aria-hidden
      className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[#6C09F7]"
      style={FLASH_ANIMATION_STYLE}
    />
  );
}

export function DaySeparator({ iso }: { iso: string }) {
  return (
    <div className="my-4 flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground first:mt-0">
      <div className="h-px flex-1 bg-muted" />
      <span>{formatDayLabel(iso)}</span>
      <div className="h-px flex-1 bg-muted" />
    </div>
  );
}

function rejecterLabel(entry: ChatThreadEntry, outgoingActor: string): string {
  if (isMine(entry.actor, outgoingActor)) return OWN_SENDER_LABEL;
  return actorLabel(entry.actor);
}

export function RejectionEntry({ entry, outgoingActor }: { entry: ChatThreadEntry; outgoingActor: string }) {
  return (
    <div className="my-3 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-rose-600 text-white">
        <X size={16} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-extrabold uppercase tracking-wider text-rose-900">
          Rejected by {rejecterLabel(entry, outgoingActor)}
        </div>
        <div className="mt-0.5 text-[11px] font-medium text-rose-700">{formatTime(entry.created_at)}</div>
        {entry.body ? (
          <p className="mt-1.5 whitespace-pre-wrap wrap-break-word text-[13px] leading-relaxed text-rose-900">{entry.body}</p>
        ) : null}
      </div>
    </div>
  );
}
